import React from 'react';
import { Avatar } from 'primereact/avatar';
import { AvatarGroup } from 'primereact/avatargroup';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { DataView } from 'primereact/dataview';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Skeleton } from 'primereact/skeleton';
import { ScrollTop } from 'primereact/scrolltop';

import SectionCard from '../../../components/agentProfile/SectionCard';

import { initialsFrom, colorFor, isOnline, subtitleFor, formatTimeSince, SORT_OPTIONS, FILTER_PRESETS } from '../utils';
import EmptyInline from '../EmptyInline';

/* ─── ConnectionItem (section-internal sub-component) ────────────────
   Single connection row used by the DataView. Two layouts:
     - "list": horizontal contact-row (LinkedIn / WhatsApp / Telegram).
       Avatar | name+subtitle+meta | inline Message button.
     - "grid": card-style tile with stacked avatar/name/subtitle, optional
       location, optional mutual face-pile, centered Message button.
   Whole tile keyboard-clickable → fires onView(profile_id). The Message
   button stops propagation and fires onMessage(profile_id) instead. */
const ConnectionItem = ({ connection, layout, connections, onView, onMessage }) => {
    const stop = (e) => e.stopPropagation();
    const p = connection.other_party || {};
    const color = colorFor(p.profile_id);
    const online = isOnline(p.profile_id);
    const isRecent =
        connection.accepted_at &&
        new Date(connection.accepted_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const mutualFaces = connections
        .filter((x) => x.id !== connection.id)
        .slice(0, 3)
        .map((x) => x.other_party)
        .filter(Boolean);

    if (layout === 'grid') {
        return (
            <div className="col-12 sm:col-6 lg:col-4 p-2">
                <div
                    className="surface-0 shadow-1 border-round-xl p-3 h-full flex flex-column transition-all transition-duration-200 hover:shadow-3 cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onClick={() => onView?.(p.profile_id)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onView?.(p.profile_id);
                        }
                    }}
                >
                    <div className="flex flex-column align-items-center text-center gap-2 mb-2">
                        <div className="relative">
                            <Avatar
                                label={initialsFrom(p.full_name)}
                                image={p.profile_photo_url_full || p.profile_photo_url || p.profile_photo || p.avatar || p.photo_url || null}
                                shape="circle"
                                style={{
                                    width: '3rem',
                                    height: '3rem',
                                    backgroundColor: `var(--${color}-100)`,
                                    color: `var(--${color}-700)`,
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    border: '2px solid var(--surface-0)',
                                    boxShadow: `0 0 0 2px var(--${color}-200)`,
                                }}
                            />
                            {online && (
                                <span
                                    className="absolute border-circle"
                                    style={{
                                        width: '0.7rem',
                                        height: '0.7rem',
                                        bottom: 0,
                                        right: 0,
                                        backgroundColor: 'var(--green-500)',
                                        border: '2px solid var(--surface-0)',
                                    }}
                                    aria-label="Online"
                                />
                            )}
                        </div>
                        <div className="min-w-0 w-full">
                            <div className="flex align-items-center justify-content-center gap-1 flex-wrap">
                                <span className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                    {p.full_name || 'Unknown'}
                                </span>
                                {isRecent && (
                                    <Tag
                                        value="New"
                                        severity="success"
                                        style={{ fontSize: '0.55rem', padding: '0.05rem 0.35rem' }}
                                    />
                                )}
                            </div>
                            <div
                                className="text-600 text-xs line-height-3 mt-1"
                                style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    minHeight: '2.1em',
                                }}
                            >
                                {subtitleFor(p)}
                            </div>
                            {(connection.connected_at || connection.accepted_at) && (
                                <div className="flex align-items-center justify-content-center gap-1 text-400 text-xs mt-1">
                                    <i className="pi pi-link" style={{ fontSize: '0.6rem' }} aria-hidden="true" />
                                    Connected {formatTimeSince(connection.connected_at || connection.accepted_at)}
                                </div>
                            )}
                        </div>
                    </div>
                    {p.location_city && (
                        <div className="flex align-items-center justify-content-center text-500 text-xs mb-2">
                            <i className="pi pi-map-marker mr-1" style={{ fontSize: '0.6rem' }} />
                            {p.location_city}
                        </div>
                    )}
                    {p.mutual_count > 0 && mutualFaces.length > 0 && (
                        <div className="flex align-items-center justify-content-center gap-2 mb-3">
                            <AvatarGroup>
                                {mutualFaces.map((m, i) => {
                                    const c = colorFor(m.profile_id);
                                    return (
                                        <Avatar
                                            key={m.profile_id || i}
                                            label={initialsFrom(m.full_name)}
                                            shape="circle"
                                            style={{
                                                width: '1.4rem',
                                                height: '1.4rem',
                                                backgroundColor: `var(--${c}-100)`,
                                                color: `var(--${c}-700)`,
                                                fontWeight: 700,
                                                fontSize: '0.5rem',
                                                border: '2px solid var(--surface-0)',
                                            }}
                                        />
                                    );
                                })}
                            </AvatarGroup>
                            <span className="text-500 text-xs">
                                <strong className="text-700">{p.mutual_count}</strong> mutual
                            </span>
                        </div>
                    )}
                    <div
                        className="flex justify-content-center mt-auto"
                        onClick={stop}
                    >
                        <Button
                            label="Message"
                            icon="pi pi-send"
                            size="small"
                            text
                            rounded
                            onClick={(e) => { e.stopPropagation(); onMessage(p.profile_id); }}
                            aria-label="Message"
                        />
                    </div>
                </div>
            </div>
        );
    }

    // List layout — single horizontal row on every viewport (LinkedIn /
    // WhatsApp / Telegram contact-row pattern). Buttons stay inline on the
    // right, icon-only on mobile with labels appearing from md+ up.
    return (
        <div className="col-12 p-2">
            <div
                className="surface-0 shadow-1 border-round-xl p-3 flex align-items-center gap-3 transition-all transition-duration-200 hover:shadow-3 cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => onView?.(p.profile_id)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onView?.(p.profile_id);
                    }
                }}
            >
                <div className="relative flex-shrink-0">
                    <Avatar
                        label={initialsFrom(p.full_name)}
                        image={p.profile_photo_url_full || p.profile_photo_url || p.profile_photo || p.avatar || p.photo_url || null}
                        shape="circle"
                        style={{
                            width: '3rem',
                            height: '3rem',
                            backgroundColor: `var(--${color}-100)`,
                            color: `var(--${color}-700)`,
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            border: '2px solid var(--surface-0)',
                            boxShadow: `0 0 0 2px var(--${color}-200)`,
                        }}
                    />
                    {online && (
                        <span
                            className="absolute border-circle"
                            style={{
                                width: '0.7rem',
                                height: '0.7rem',
                                bottom: 0,
                                right: 0,
                                backgroundColor: 'var(--green-500)',
                                border: '2px solid var(--surface-0)',
                            }}
                            aria-label="Online"
                        />
                    )}
                </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex align-items-baseline justify-content-between gap-2">
                            <div className="flex align-items-center gap-2 flex-wrap min-w-0">
                                <span className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                    {p.full_name || 'Unknown'}
                                </span>
                                {isRecent && (
                                    <Tag
                                        value="New"
                                        severity="success"
                                        style={{ fontSize: '0.55rem', padding: '0.05rem 0.35rem' }}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="text-600 text-xs line-height-3 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {subtitleFor(p)}
                        </div>
                        <div className="flex align-items-center gap-3 mt-2 flex-wrap">
                            {(connection.connected_at || connection.accepted_at) && (
                                <span className="text-500 text-xs">
                                    <i className="pi pi-link mr-1" style={{ fontSize: '0.6rem' }} aria-hidden="true" />
                                    Connected {formatTimeSince(connection.connected_at || connection.accepted_at)}
                                </span>
                            )}
                            {p.location_city && (
                                <span className="text-500 text-xs">
                                    <i className="pi pi-map-marker mr-1" style={{ fontSize: '0.6rem' }} />
                                    {p.location_city}
                                </span>
                            )}
                            {p.mutual_count > 0 && mutualFaces.length > 0 && (
                                <div className="flex align-items-center gap-1">
                                    <AvatarGroup>
                                        {mutualFaces.map((m, i) => {
                                            const c = colorFor(m.profile_id);
                                            return (
                                                <Avatar
                                                    key={m.profile_id || i}
                                                    label={initialsFrom(m.full_name)}
                                                    shape="circle"
                                                    style={{
                                                        width: '1.4rem',
                                                        height: '1.4rem',
                                                        backgroundColor: `var(--${c}-100)`,
                                                        color: `var(--${c}-700)`,
                                                        fontWeight: 700,
                                                        fontSize: '0.5rem',
                                                        border: '2px solid var(--surface-0)',
                                                    }}
                                                />
                                            );
                                        })}
                                    </AvatarGroup>
                                    <span className="text-500 text-xs ml-1">
                                        <strong className="text-700">{p.mutual_count}</strong> mutual
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                <div className="flex-shrink-0" onClick={stop}>
                    <Button
                        icon="pi pi-send"
                        label="Message"
                        size="small"
                        text
                        rounded
                        onClick={(e) => { e.stopPropagation(); onMessage(p.profile_id); }}
                        aria-label="Message"
                        pt={{
                            label: { className: 'hidden md:inline ml-1' },
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

/* ─── ConnectionsListBody (section-internal sub-component) ───────────
   Compact / expanded body for the connections list:
     - Compact:   first 5 + "Show more" text button.
     - Expanded:  scroll container, hidden scrollbar, frosted ScrollTop
                  FAB anchored to container at bottom-right. "Show less"
                  text button below container.
   Uses DataView in both modes so list-vs-grid layout toggle keeps
   working. */
const ConnectionsListBody = ({
    connections, connectionsTotal,
    viewMode,
    expanded, onExpand, onCollapse,
    scrollRef, sentinelRef,
    hasMore, loadingMore,
    onView, onMessage,
}) => {
    /* Compact mode: first 5 + "Show more" button. */
    if (!expanded) {
        const preview = connections.slice(0, 5);
        return (
            <>
                <DataView
                    value={preview}
                    layout={viewMode}
                    itemTemplate={(c) => (
                        <ConnectionItem
                            connection={c}
                            layout={viewMode}
                            connections={connections}
                            onView={onView}
                            onMessage={onMessage}
                        />
                    )}
                    pt={{
                        grid:    { className: 'grid' },
                        content: { className: 'border-none p-0' },
                        header:  { className: 'hidden' },
                    }}
                />
                {(connections.length > 5 || connectionsTotal > connections.length) && (
                    <div className="text-center pt-3 border-top-1 surface-border">
                        <Button
                            label="Show more"
                            icon="pi pi-chevron-down"
                            iconPos="right"
                            text
                            size="small"
                            onClick={onExpand}
                        />
                    </div>
                )}
            </>
        );
    }

    /* Expanded mode: scroll container, hidden scrollbar, frosted FAB. */
    return (
        <>
            <style>{`
                .lending-connections-scroll::-webkit-scrollbar { display: none; }
                .lending-connections-scrolltop {
                    bottom: 0.75rem !important;
                    right: 0.75rem !important;
                    background-color: rgba(120, 120, 128, 0.32) !important;
                    backdrop-filter: blur(12px) saturate(180%);
                    -webkit-backdrop-filter: blur(12px) saturate(180%);
                    border: 1px solid rgba(255, 255, 255, 0.4) !important;
                    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18) !important;
                    width: 2.5rem !important;
                    height: 2.5rem !important;
                }
                .lending-connections-scrolltop .pi {
                    color: #1f2937 !important;
                    font-size: 1rem !important;
                    font-weight: 700 !important;
                }
            `}</style>

            <div
                ref={scrollRef}
                className="lending-connections-scroll"
                style={{
                    maxHeight: '32rem',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    position: 'relative',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                }}
            >
                <DataView
                    value={connections}
                    layout={viewMode}
                    itemTemplate={(c) => (
                        <ConnectionItem
                            connection={c}
                            layout={viewMode}
                            connections={connections}
                            onView={onView}
                            onMessage={onMessage}
                        />
                    )}
                    pt={{
                        grid:    { className: 'grid' },
                        content: { className: 'border-none p-0' },
                        header:  { className: 'hidden' },
                    }}
                />
                {hasMore && (
                    <div ref={sentinelRef} className="text-center py-3">
                        {loadingMore ? (
                            <span className="text-500 text-xs">
                                <i className="pi pi-spin pi-spinner mr-1" aria-hidden="true" />
                                Loading more…
                            </span>
                        ) : (
                            <span className="text-400 text-xs">Scroll for more</span>
                        )}
                    </div>
                )}
                {!hasMore && connections.length > 5 && (
                    <div className="text-center py-3 text-400 text-xs">
                        You've reached the end · {connectionsTotal} connections
                    </div>
                )}
                <ScrollTop
                    target="parent"
                    threshold={200}
                    icon="pi pi-arrow-up"
                    behavior="smooth"
                    className="lending-connections-scrolltop"
                />
            </div>

            <div className="text-center pt-3 border-top-1 surface-border">
                <Button
                    label="Show less"
                    icon="pi pi-chevron-up"
                    iconPos="right"
                    text
                    size="small"
                    onClick={onCollapse}
                />
            </div>
        </>
    );
};

/**
 * MyConnectionsCard — accepted-connections section of the main column.
 * Header has a list/grid view-mode toggle. Body has search input,
 * sort dropdown, filter-preset chips, then the connections list (compact
 * preview / expanded scrollable).
 *
 * State stays lifted to MyNetwork.jsx — this component is presentational +
 * receives its data + handlers as props.
 *
 * Props:
 *   connections          — currently-loaded connections page-accumulated array
 *   connectionsTotal     — server total for this filter set
 *   connectionsLoading   — initial load skeleton flag
 *   connectionsHasMore   — whether more pages remain
 *   connectionsLoadingMore — appending-next-page flag
 *   connectionsScrollRef — scroll container ref (for ScrollTop FAB target)
 *   connectionsSentinelRef — IntersectionObserver sentinel ref
 *   connectionsCardRef   — the card wrapper ref (for "scroll to me")
 *   connectionsExpanded  — compact (5) / expanded (scrolling list) flag
 *   onExpand / onCollapse
 *   connectionsSearch    — search input value
 *   onSearchChange       — (val: string) => void
 *   sortBy / onSortChange
 *   activeFilters / onToggleFilter / onClearFilters
 *   viewMode / onViewModeChange  — 'list' | 'grid'
 *   onView      — (profileId) => void
 *   onMessage   — (profileId) => void
 */
const MyConnectionsCard = ({
    connections, connectionsTotal,
    connectionsLoading,
    connectionsHasMore, connectionsLoadingMore,
    connectionsScrollRef, connectionsSentinelRef, connectionsCardRef,
    connectionsExpanded, onExpand, onCollapse,
    connectionsSearch, onSearchChange,
    sortBy, onSortChange,
    activeFilters, onToggleFilter, onClearFilters,
    viewMode, onViewModeChange,
    onView, onMessage,
}) => (
    <div id="connections-section" ref={connectionsCardRef}>
        <SectionCard
            icon="pi-users"
            title={`My connections (${connectionsTotal})`}
            headerAction={
                <div className="flex align-items-center gap-1">
                    <Button
                        icon="pi pi-list"
                        size="small"
                        severity="secondary"
                        text={viewMode !== 'list'}
                        onClick={() => onViewModeChange('list')}
                        aria-label="List view"
                    />
                    <Button
                        icon="pi pi-th-large"
                        size="small"
                        severity="secondary"
                        text={viewMode !== 'grid'}
                        onClick={() => onViewModeChange('grid')}
                        aria-label="Grid view"
                    />
                </div>
            }
            className="mb-3"
        >
            {/* Search + sort */}
            <div className="grid formgrid mb-2">
                <div className="col-12 md:col-7">
                    <IconField iconPosition="left">
                        <InputIcon className="pi pi-search" />
                        <InputText
                            value={connectionsSearch}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search in your connections…"
                            className="w-full p-inputtext-sm"
                        />
                    </IconField>
                </div>
                <div className="col-12 md:col-5">
                    <Dropdown
                        value={sortBy}
                        options={SORT_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => onSortChange(e.value)}
                        className="w-full p-inputtext-sm"
                    />
                </div>
            </div>

            {/* Filter chips — pill shape, horizontal-scroll on mobile */}
            <div
                className="overflow-x-auto mb-3"
                style={{ whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch' }}
            >
                <div
                    className="flex gap-2 align-items-center"
                    style={{ minWidth: 'fit-content' }}
                >
                    {FILTER_PRESETS.map((f) => {
                        const active = activeFilters.includes(f.label);
                        return (
                            <button
                                key={f.label}
                                type="button"
                                onClick={() => onToggleFilter(f.label)}
                                className="flex-shrink-0 cursor-pointer flex align-items-center gap-2 transition-colors transition-duration-150"
                                style={{
                                    padding: '0.4rem 0.85rem',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    borderRadius: '999px',
                                    border: active
                                        ? '1px solid var(--primary-color)'
                                        : '1px solid var(--surface-300)',
                                    backgroundColor: active
                                        ? 'var(--primary-color)'
                                        : 'var(--surface-0)',
                                    color: active
                                        ? 'var(--primary-color-text)'
                                        : 'var(--text-color)',
                                }}
                            >
                                {f.label}
                                {active && (
                                    <i
                                        className="pi pi-times"
                                        style={{ fontSize: '0.7rem' }}
                                        aria-hidden="true"
                                    />
                                )}
                            </button>
                        );
                    })}
                    {activeFilters.length > 0 && (
                        <Button
                            label="Clear all"
                            size="small"
                            text
                            onClick={onClearFilters}
                            style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}
                        />
                    )}
                </div>
            </div>

            {/* Initial-load skeletons / empty / list */}
            {connectionsLoading && connections.length === 0 ? (
                <div className="flex flex-column gap-2">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} width="100%" height="4rem" />
                    ))}
                </div>
            ) : connections.length === 0 ? (
                <EmptyInline
                    icon="pi-users"
                    title={
                        connectionsSearch || activeFilters.length
                            ? 'No matches'
                            : 'No connections yet'
                    }
                    description={
                        connectionsSearch || activeFilters.length
                            ? 'Try a different search or clear filters.'
                            : 'Accept invitations or browse suggestions below.'
                    }
                />
            ) : (
                <ConnectionsListBody
                    connections={connections}
                    connectionsTotal={connectionsTotal}
                    viewMode={viewMode}
                    expanded={connectionsExpanded}
                    onExpand={onExpand}
                    onCollapse={onCollapse}
                    scrollRef={connectionsScrollRef}
                    sentinelRef={connectionsSentinelRef}
                    hasMore={connectionsHasMore}
                    loadingMore={connectionsLoadingMore}
                    onView={onView}
                    onMessage={onMessage}
                />
            )}
        </SectionCard>
    </div>
);

export default React.memo(MyConnectionsCard);
